import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatDate, formatCurrencyNoDecimals } from '@/utils/formatters';
import { getStatusColor } from '../transaction-status';
import { Calendar, AlertTriangle, Building2, ReceiptText } from 'lucide-react';
import type { InvestorDrawdownDetail } from '../services/drawdowns-service';
import { PrepaymentSources } from './PrepaymentSources';

interface DrawdownDetailProps {
  drawdown: InvestorDrawdownDetail;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  wire_transfer: 'Wire Transfer',
  check: 'Check',
  ach: 'ACH',
  sepa: 'SEPA',
  swift: 'SWIFT',
};

// Shared visual tokens so every section card and status badge match exactly
const CARD_CONTENT_CLASS = 'pt-4 pb-4';
const statusBadgeClass = (status: string) =>
  `${getStatusColor(status)} font-medium`;

export function DrawdownDetail({ drawdown }: DrawdownDetailProps) {
  const capitalCallAmount = drawdown.investor_allocation.drawdown_amount;
  const invoiceAmount = drawdown.investor_allocation.invoice_amount;
  const prepaymentContribution =
    drawdown.investor_allocation.prepayment_contribution;
  const prepaymentUsed = drawdown.investor_allocation.prepayment_used;
  const amountPaid = drawdown.investor_allocation.amount_paid;
  const payments = drawdown.investor_payments || [];
  const paymentInstructions = drawdown.payment_instructions;
  const isPaid = drawdown.investor_allocation.status === 'paid';
  const isOverdue = drawdown.investor_allocation.days_overdue > 0;

  return (
    <div className="space-y-4">
      {/* Header Card - Fund, Status, Dates */}
      <Card>
        <CardContent className={CARD_CONTENT_CLASS}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-medium text-lg">{drawdown.fund_name}</h3>
              {drawdown.purpose && (
                <p className="text-sm text-muted-foreground">
                  {drawdown.purpose}
                </p>
              )}
            </div>
            <Badge
              className={statusBadgeClass(drawdown.investor_allocation.status)}
            >
              {drawdown.investor_allocation.status.toUpperCase()}
            </Badge>
          </div>

          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>Notice: {formatDate(drawdown.issue_date)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>Due: {formatDate(drawdown.due_date)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Amount Card */}
      <Card>
        <CardContent className={CARD_CONTENT_CLASS}>
          <div className="text-sm text-muted-foreground mb-1">
            Total Payment Due
          </div>
          <div className="text-2xl font-medium">
            {formatCurrencyNoDecimals(invoiceAmount)}
          </div>
          {!isPaid && amountPaid > 0 && (
            <div className="mt-1 text-sm text-muted-foreground">
              Still owed:{' '}
              <span className="font-medium text-foreground">
                {formatCurrencyNoDecimals(
                  drawdown.investor_allocation.amount_due
                )}
              </span>
              <span className="mx-2 text-border">•</span>
              Paid so far:{' '}
              <span className="font-medium text-foreground">
                {formatCurrencyNoDecimals(amountPaid)}
              </span>
            </div>
          )}

          {/* Breakdown for first drawdown with prepayment contribution */}
          {prepaymentContribution > 0 && (
            <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Capital Call</span>
                <span>{formatCurrencyNoDecimals(capitalCallAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Prepayment due to regulation
                </span>
                <span>{formatCurrencyNoDecimals(prepaymentContribution)}</span>
              </div>
            </div>
          )}

          {/* Breakdown for subsequent drawdowns with prepayment used */}
          {prepaymentUsed > 0 && (
            <div className="mt-3 pt-3 border-t space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Capital Call</span>
                <span>{formatCurrencyNoDecimals(capitalCallAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Less: Prepayment Applied
                </span>
                <span>-{formatCurrencyNoDecimals(prepaymentUsed)}</span>
              </div>
              <PrepaymentSources
                sources={drawdown.prepayment_sources}
                unattributed={drawdown.prepayment_unattributed}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overdue Alert */}
      {isOverdue && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            {drawdown.investor_allocation.days_overdue} days overdue
          </AlertDescription>
        </Alert>
      )}

      {/* Payment Card */}
      <Card>
        <CardContent className={CARD_CONTENT_CLASS}>
          <div className="flex items-center gap-2 mb-3">
            <ReceiptText className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Payment</span>
          </div>

          {payments.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Date
                    </th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">
                      Amount
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Method
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Reference
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="py-2.5 px-3">
                        {formatDate(payment.payment_date)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-medium">
                        {formatCurrencyNoDecimals(payment.amount)}
                      </td>
                      <td className="py-2.5 px-3 text-muted-foreground">
                        {PAYMENT_METHOD_LABELS[payment.payment_method] ||
                          payment.payment_method}
                      </td>
                      <td className="py-2.5 px-3 text-muted-foreground font-mono text-xs">
                        {payment.reference_number || '-'}
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge
                          className={statusBadgeClass(
                            payment.processing_status
                          )}
                        >
                          {payment.processing_status.charAt(0).toUpperCase() +
                            payment.processing_status.slice(1)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No payment recorded yet
            </p>
          )}
        </CardContent>
      </Card>

      {/* Payment Instructions - Only show if not paid */}
      {!isPaid && paymentInstructions && (
        <Card>
          <CardContent className={CARD_CONTENT_CLASS}>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Payment Instructions</span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Bank</span>
                <p className="font-medium">{paymentInstructions.bank_name}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Account Name</span>
                <p className="font-medium">
                  {paymentInstructions.account_name}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">IBAN</span>
                <p className="font-mono text-xs">{paymentInstructions.iban}</p>
              </div>
              <div>
                <span className="text-muted-foreground">BIC</span>
                <p className="font-mono text-xs">{paymentInstructions.bic}</p>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Reference</span>
                <p className="font-mono text-xs">
                  {paymentInstructions.reference || '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
